/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { TFunction } from 'i18next';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  ReactHookFormProvider,
  defaultFormOptions,
} from '@opengeoweb/form-fields';
import { useConfirmationDialog } from '@opengeoweb/shared';
import type {
  ConfirmationOptions,
  DraggableSize,
  Position,
} from '@opengeoweb/shared';
import type { MapPreset, uiTypes } from '@opengeoweb/store';
import { WarningEventDialog } from './WarningEventDialog';
import type { FormError } from '../../../store/warningsEventForm/reducer';
import type { WarningEvent } from '../../../store/warningsForm/types';
import { useWarningsTranslation } from '../../../utils/i18n';
import { WarningEventFormContent } from '../WarningEventForm';
import { getFormData } from '../WarningEventForm/utils';
import { WarningEventSummaryDialog } from '../WarningEventSummaryDialog';

export const warningFormConfirmationOptions = (
  t: TFunction,
): ConfirmationOptions => ({
  cancelLabel: t('close-dialog-cancel'),
  title: t('close-warning-dialog-title'),
  description: t('close-dialog-description'),
  confirmLabel: t('close-dialog-confirm'),
  catchOnCancel: true,
});

export interface WarningEventFormDialogProps {
  defaultMapPreset?: MapPreset;
  bounds?: string;
  currentEventEditor?: string;
  shouldHideFormToggleMode?: boolean;
  selectedEvent?: WarningEvent;
  error?: FormError;
  // main event dialog
  isOpen: boolean;
  isSnapshot?: boolean;
  isReadOnly?: boolean;
  order?: number;
  size?: DraggableSize;
  source?: uiTypes.Source;
  startPosition?: Position;
  onClose: () => void;
  onChangeFormMode?: (isReadOnly: boolean) => void;
  onMouseDown?: () => void;
  onDelete?: () => void;
  onFormDirty?: (isFormDirty: boolean) => void;
  onSaveDraft?: (formValues: WarningEvent) => Promise<Headers | null>;
  onPublish?: (formValues: WarningEvent) => Promise<Headers | null>;
  // summary dialog
  isSummaryOpen: boolean;
  summaryOrder?: number;
  summarySource?: uiTypes.Source;
  startPositionSummary?: Position;
  onOpenSummary: () => void;
  onCloseSummary: () => void;
  onAddSummary: () => void;
  onMouseDownSummary?: () => void;
}

type WarningEventFormDialogContentProps = Omit<
  WarningEventFormDialogProps,
  'initialValues'
>;

const WarningEventFormDialogContent: React.FC<
  WarningEventFormDialogContentProps
> = ({
  defaultMapPreset,
  bounds,
  currentEventEditor,
  error = { severity: 'error', message: '' },
  isOpen,
  isReadOnly = false,
  order,
  size,
  source,
  startPosition,
  onClose,
  onMouseDown,
  onChangeFormMode = (): void => {},
  onDelete = (): void => {},
  onFormDirty = (): void => {},
  onSaveDraft = async (formValues: WarningEvent): Promise<null> => null,
  onPublish = async (formValues: WarningEvent): Promise<null> => null,
  isSummaryOpen,
  summaryOrder,
  summarySource,
  startPositionSummary,
  shouldHideFormToggleMode = false,
  selectedEvent,
  onOpenSummary,
  onCloseSummary,
  onAddSummary,
  onMouseDownSummary,
  isSnapshot = false,
}) => {
  const { t } = useWarningsTranslation();
  const {
    formState: { dirtyFields },
  } = useFormContext();
  const isFormDirty = Object.keys(dirtyFields).length > 0;

  const confirmDialog = useConfirmationDialog();
  const closeDialog = async (): Promise<void> => {
    if (isFormDirty) {
      const mayProceed = await confirmDialog(warningFormConfirmationOptions(t))
        .then(() => true)
        .catch(() => false);

      if (!mayProceed) {
        return;
      }
    }
    onClose();
  };

  return (
    <>
      <WarningEventDialog
        bounds={bounds}
        isOpen={isOpen}
        onClose={closeDialog}
        onMouseDown={onMouseDown}
        order={order}
        size={size}
        source={source}
        startPosition={startPosition}
      >
        <WarningEventFormContent
          currentEventEditor={currentEventEditor}
          defaultMapPreset={defaultMapPreset}
          error={error}
          isReadOnly={isReadOnly}
          isSnapshot={isSnapshot}
          isSummaryDialogOpen={isSummaryOpen}
          onCancel={closeDialog}
          onChangeFormMode={onChangeFormMode}
          onDelete={onDelete}
          onFormDirty={onFormDirty}
          onOpenWarningSummary={onOpenSummary}
          onPublish={onPublish}
          onSaveDraft={onSaveDraft}
          selectedEvent={selectedEvent}
          shouldHideFormToggleMode={shouldHideFormToggleMode}
        />
      </WarningEventDialog>
      <WarningEventSummaryDialog
        bounds={bounds}
        isOpen={isSummaryOpen}
        isReadOnly={isReadOnly}
        onClickAdd={onAddSummary}
        onClose={onCloseSummary}
        onMouseDown={onMouseDownSummary}
        order={summaryOrder}
        source={summarySource}
        startPosition={startPositionSummary}
      />
    </>
  );
};

export const WarningEventFormDialog: React.FC<WarningEventFormDialogProps> = ({
  selectedEvent,
  isOpen,
  ...contentProps
}) => {
  // don't render the form if the dialog is closed so we get a fresh form with actual fresh values and dates
  if (!isOpen) {
    return null;
  }

  return (
    <ReactHookFormProvider
      options={{
        ...defaultFormOptions,
        mode: 'onSubmit',
        defaultValues: getFormData(selectedEvent),
      }}
    >
      <WarningEventFormDialogContent
        isOpen={isOpen}
        {...contentProps}
        selectedEvent={selectedEvent}
      />
    </ReactHookFormProvider>
  );
};
